<script setup lang="ts">
  import { useMainHeaderStore } from '@/components/main-header/store'
  import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'
  import type { LinkStoryblok } from '@/storyblok/component-types-sb'
  import { CAMPAIGN_COUNTER_COOKIE_NAME } from '@/utils/constants/cookies'
  import type {
    IHeaderMobileAppLink,
    IHeaderSection,
    IHeaderTabs,
    IMainNavigation
  } from '@/components/main-header/types'
  import { SB_DATE_FORMAT, RIGA_TIMEZONE } from '@/utils/constants/date'

  withDefaults(defineProps<{ isIndex?: boolean }>(), { isIndex: false })

  const mainHeaderStore = useMainHeaderStore()
  const globalSettingsStore = useGlobalSettingsStore()
  const { isDesktop } = useBTBreakpoints()
  const { locale } = useI18n()

  const { data, error } = await useFetch<IMainNavigation>('/api/storyblok/main-navigation', {
    query: { language: locale.value }
  })

  const tabs = ref<IHeaderTabs[]>([])
  const sections = ref<IHeaderSection[]>([])
  const mobileAppLinks = ref<IHeaderMobileAppLink[]>([])

  const { isMenuOpen } = storeToRefs(mainHeaderStore)
  const { globalAlert } = storeToRefs(globalSettingsStore)
  const hasGlobalAlert = useState<boolean>('hasGlobalAlert', () => false)
  const isVisibleGlobalAlert = ref(true)

  if (!error.value) {
    tabs.value = data.value?.tabs ?? []
    sections.value = data.value?.sections ?? []
    mobileAppLinks.value = data.value?.mobileAppLinks ?? []
  } else {
    if (isDev()) {
      console.error('Error fetching main navigation data:', error.value)
    }
  }

  watchEffect(() => {
    hasGlobalAlert.value = !!(globalAlert.value && isVisibleGlobalAlert.value)
  })

  const globalAlertProps = computed(
    () =>
      globalAlert.value || {
        isBold: false,
        closable: false,
        showDefaultIcon: false,
        type: 'info',
        content: '',
        icon: null
      }
  )

  const isScrollLocked = useScrollLock(document)

  const currentRouteFullPath = trimLastSlash(useRouter().currentRoute.value.fullPath)

  const { getCurrentTime } = useCurrentTime()
  const currentTime = await getCurrentTime()

  const isCampaignCounterHidden = useCookie<boolean>(CAMPAIGN_COUNTER_COOKIE_NAME)

  const campaignCounterStory = await useFetchDataFromStoryblok('main-navigation/campaign-timer', [
    'campaign-counter.excludedFrom',
    'campaign-counter.ctaLink'
  ])

  const dayjs = useDayjs()

  const campaignStartDate = dayjs(currentTime)
  const campaignEndDate = computed(() => {
    return dayjs
      .tz(campaignCounterStory.value?.content?.expirationDateTime, SB_DATE_FORMAT, RIGA_TIMEZONE)
      .add(59, 'seconds')
  })

  const shouldShowCampaignCounter = computed(() => {
    const excludedLinks = campaignCounterStory.value?.content?.excludedFrom || []

    if (campaignStartDate.isAfter(campaignEndDate.value, 'seconds')) return false

    if (isCampaignCounterHidden.value === true && !isDesktop.value) return false

    return !excludedLinks.some(
      (excludedPage: LinkStoryblok) =>
        currentRouteFullPath.includes(excludedPage.content.href.url) ||
        currentRouteFullPath.includes(excludedPage.content.href.cached_url)
    )
  })

  provide('campaignDates', {
    book_date_start: null,
    book_date_end: null,
    travel_date_start: null,
    travel_date_end: null,
    updatedIsCampaignActive: (isActive: boolean) => {
      isCampaignCounterHidden.value = !isActive
    },
    currentTime: currentTime
  })

  watch(
    () => mainHeaderStore.activeSection,
    (value) => (mainHeaderStore.isMenuOpen = !!value)
  )

  watch(
    () => mainHeaderStore.isMenuOpen,
    (value) => (isScrollLocked.value = !!value)
  )

  watch(isCampaignCounterHidden, (value) => {
    isCampaignCounterHidden.value = value
  })
</script>
<template>
  <header class="sticky top-0 z-30 bg-white">
    <UiSkipToContent />
    <MainHeaderTopBar :header-data="{ tabs, sections, mobileAppLinks }" />
  </header>
  <LazyMainHeaderMobileSidebarBottomMenuBar
    v-if="!isDesktop && !isMenuOpen"
    :tabs="tabs"
  />
  <div
    v-if="hasGlobalAlert"
    class="mx-2 my-4 md:mx-10 md:my-6"
  >
    <LazyUiAlertGlobal
      v-model="isVisibleGlobalAlert"
      v-bind="globalAlertProps"
      class="m-auto w-full max-w-460 rounded-sm"
    />
  </div>

  <LazyMainHeaderCampaignTimer
    v-if="shouldShowCampaignCounter && campaignCounterStory"
    :blok="campaignCounterStory"
  />
</template>
